import { APIError } from "@certn/api";
import { PrimaryTag } from "@certn/cascadia-react";
import { ActionListControl } from "@certn/forms/src/controls/ActionListControl";
import { ArrayControlProps } from "@jsonforms/core";
import { useTranslation } from "react-i18next";

import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { updateSection, removeItems, useHandleChange } from "../../utils";

const getIn = (value: any, keys: (string | number)[]) =>
  keys.reduce((acc, key) => acc?.[key], value);

type EducationItem = Record<string, unknown>;

/**
 * Normalise nullable boolean fields that default to `false` so the dialog
 * always opens with a real boolean in `initialValues`.  Without this, a
 * backend-returned `null` reaches the JSON Forms store and the controlled
 * `<Checkbox isSelected={null} …>` drifts into an uncontrolled state,
 * making it unresponsive to user clicks even after the `?? false` fix
 * in CheckboxControl.
 */
function normalizeEducationItem(item: EducationItem): EducationItem {
  return {
    ...item,
    is_highest_education:
      (item.is_highest_education as boolean | null) ?? false,
  };
}

export function EducationHistoryControl(props: ArrayControlProps) {
  const { caseId } = useApplicationFormContext();
  const { currentSection, basePath } = useApplication();
  const { t } = useTranslation();
  const handleChange = useHandleChange();
  const dataSection = ["education_history", "educations"];

  const normalizedData: EducationItem[] | undefined = props.data?.map(
    normalizeEducationItem
  );

  return (
    <ActionListControl
      {...props}
      data={normalizedData}
      alwaysDisplayRequirementsAsBullets
      renderTag={({
        date_awarded,
        is_currently_attending,
        is_highest_education,
        qualification_not_obtained,
      }) => {
        return (
          <>
            {is_highest_education ? (
              <PrimaryTag type="subtle">
                {t("Highest completed qualification")}
              </PrimaryTag>
            ) : null}
            {!date_awarded && is_currently_attending ? (
              <PrimaryTag type="subtle">{t("Current")}</PrimaryTag>
            ) : null}
            {qualification_not_obtained ? (
              <PrimaryTag type="warn">
                {t("Qualification not obtained")}
              </PrimaryTag>
            ) : null}
          </>
        );
      }}
      onItemChanged={async (parentValues = [], values, index) => {
        const educationValues = values as {
          is_highest_education?: boolean;
          is_currently_attending?: boolean;
          qualification_not_obtained?: boolean;
        };

        // Highest completed qualification is incompatible with currently attending
        // or qualification not obtained — clear it automatically when those are set.
        const isIncompatibleWithHighest =
          educationValues.is_currently_attending ||
          educationValues.qualification_not_obtained;
        const normalizedValues = isIncompatibleWithHighest
          ? { ...values, is_highest_education: false }
          : values;

        const isNewItem = index >= parentValues.length;
        const mappedExisting = parentValues.map((item, itemIndex) => {
          if (itemIndex === index) {
            return normalizedValues;
          }
          if (
            (normalizedValues as typeof educationValues).is_highest_education
          ) {
            return { ...item, is_highest_education: false };
          }
          return item;
        });
        const educations = isNewItem
          ? [...mappedExisting, normalizedValues]
          : mappedExisting;

        const updatedArray = {
          education_history: { educations },
        };
        if (caseId) {
          try {
            await updateSection(
              caseId,
              updatedArray,
              currentSection,
              true,
              basePath
            );
          } catch (e) {
            if (e instanceof APIError) {
              return getIn(e.body, [...dataSection, index]);
            }
          }
        }

        // Sync sibling "Highest education" flags in the local JSON Forms store.
        // Use mappedExisting (not educations) so new items aren't pre-inserted —
        // ActionListControl.addItem handles the actual insertion after
        // onItemChanged returns, and using educations here would duplicate it.
        handleChange(props.path, mappedExisting);
      }}
      onItemRemoved={async (parentValues, indexes) => {
        const updatedArray = removeItems(dataSection, parentValues, indexes);
        if (updatedArray) {
          if (caseId) {
            await updateSection(
              caseId,
              updatedArray,
              currentSection,
              true,
              basePath
            );
          }
        }
      }}
    />
  );
}
